import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import { isPrefileActive } from '../utils/prefile';
import { useWizardFormId, withForm, formBelongsToCompany } from '../utils/wizardForm';


const stripePromise = loadStripe( 
  'pk_live_51QFkHWDxSnLolRmeUoubokLeso1STh2OklVe5rzLwVR87JZUwkhdgous1ePR1SxzRXRGcLbLmklnscUdMa3i1znz00Zcc28Jqm'
  //'pk_test_51QX3fKG8KGwvZ4lzrjAvbwHlayyXgDFYzjdtYfpqRdtwtPcQAsJGrVOWmdxbAspafJ4EYAPL5ijXOlLErtC0cZz300y2blY02q'
);
function CheckoutForm({ clientSecret, consent, onConsentChange, onSuccessNavigate, formId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const { id: companyId } = useParams();
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consent) {
      setError('You must agree to the disclosure before submitting.');
      return;
    }
    if (!stripe || !elements) return;

    setProcessing(true);
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}${withForm(`/company/${companyId}/file-form/irs-confirmation`, formId)}`
      },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message);
      setProcessing(false);
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccessNavigate();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="px-6 py-4">
      <PaymentElement />
      {error && <p className="mt-2 text-red-600 text-sm">{error}</p>}

      <div className="flex items-start space-x-2 mt-4">
        <input
          type="checkbox"
          id="consent"
          className="mt-1 h-4 w-4 text-[#0F2C5E] border-gray-300 focus:ring-[#0F2C5E]"
          checked={consent}
          onChange={e => onConsentChange(e.target.checked)}
        />
        <label htmlFor="consent" className="text-sm">
          I agree to the disclosure on Form 2290 and Schedule 1 / Form 8849.{' '}
          <a href={`/company/${companyId}/file-form/disclosure`} className="text-blue-600 underline hover:text-blue-800">
            Read disclosure terms
          </a>
        </label>
      </div>

      <button
        type="submit"
        disabled={!stripe || processing}
        className="mt-6 bg-[#0F2C5E] text-white px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {processing ? 'Processing…' : 'Pay & Submit'}
      </button>
    </form>
  );
}

function PrefileWarningModal({ onAccept, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white shadow-lg max-w-lg w-full">
        <div className="bg-[#0F2C5E] text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-base font-semibold">Future Tax year (July2026 - June2027)</h2>
          <button onClick={onCancel} className="text-white hover:text-gray-300 text-xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm text-gray-800">
          <p className="text-lg font-semibold text-[#0F2C5E]">
            You are Pre-filing your 2290 tax returns for the year 2026 - 2027.
          </p>
          <p>
            We will automatically submit your 2290 return to the IRS once the IRS starts
            accepting e-filed 2290 returns for the tax year July2026 - June2027. This
            submission will be considered as advanced filing and the tax return will be
            processed once the IRS start issuing the stamped Schedule 1 electronically.
            Click Accept to continue.
          </p>
        </div>
        <div className="px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="bg-red-500 text-white px-5 py-2 hover:bg-red-600 transition"
          >
            Cancel
          </button>
          <button
            onClick={onAccept}
            className="bg-[#0F2C5E] text-white px-5 py-2 hover:bg-blue-900 transition"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PaymentAndSubmission() {
  const { id: ID } = useParams();
  const navigate = useNavigate();
  // Номер формы — из адреса этой вкладки (см. utils/wizardForm.js). Здесь это
  // особенно важно: по нему списываются деньги.
  const formId = useWizardFormId();
  const [paymentError, setPaymentError] = useState('');
  const [processingFee, setProcessingFee] = useState(0);
  const [clientSecret, setClientSecret] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [taxPeriod, setTaxPeriod] = useState('');
  const [prefileAccepted, setPrefileAccepted] = useState(false);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitError, setResubmitError] = useState('');

  const steps = [
    { id: 1, label: 'Tax & Form' },
    { id: 2, label: 'Vehicles Tax' },
    { id: 3, label: 'IRS Payment Options' },
    { id: 4, label: 'Form Summary' },
    { id: 5, label: 'Payment & Submission' },
    { id: 6, label: 'IRS Confirmation' },
  ];
  const currentStep = 5;

  useEffect(() => {
  let isMounted = true;

  const init = async () => {
    if (!isMounted) return; // отменяем если размонтировано
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!formId) return; // useWizardFormId уже уводит на страницу компании

      const res1 = await fetch(`/api/form2290/${formId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res1.ok) throw new Error('Error loading form data');
      const data = await res1.json();
      if (!formBelongsToCompany(data, ID)) {
        navigate(`/company/${ID}`, { replace: true });
        return;
      }
      setTaxPeriod(data.tax_period || '');
      const count = Array.isArray(data.vehicles) ? data.vehicles.length : 0;
      let fee;
      if (count === 1) fee = 19.99;
      else if (count <= 24) fee = 24.99;
      else if (count <= 100) fee = 34.99;
      else if (count <= 250) fee = 49.99;
      else if (count <= 1000) fee = 74.99;
      else fee = 99.99;

      setProcessingFee(fee);

      if (data.payment_status) {
        setAlreadyPaid(true);
        return;
      }

      const res2 = await fetch(
        `/api/form2290/${formId}/create-payment-intent/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ processing_fee: fee })
        }
      );
      if (!res2.ok) {
        // Сервер объясняет причину (например, в форме нет машин) — показываем её,
        // а не пустую форму Stripe без clientSecret.
        const err = await res2.json().catch(() => ({}));
        setPaymentError(err.error || err.detail || 'Unable to start the payment. Please try again.');
        return;
      }
      const { clientSecret } = await res2.json();
      setClientSecret(clientSecret);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  init();

  return () => {
    isMounted = false;
  };
}, [formId, ID, navigate]);

  const handleBack = () => {
    navigate(withForm(`/company/${ID}/file-form/form-summary`, formId));
  };

  const handleResubmitToIrs = async () => {
    setResubmitError('');
    if (!consent) {
      setResubmitError('You must agree to the disclosure before submitting.');
      return;
    }
    setResubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/form2290/${formId}/submit/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to submit to IRS');
      }
      navigate(withForm(`/company/${ID}/file-form/irs-confirmation`, formId));
    } catch (e) {
      setResubmitError(e.message);
    } finally {
      setResubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading payment form…</p>
      </div>
    );
  }

  const isPrefile = isPrefileActive(taxPeriod);

  return (
    <div className="flex flex-col min-h-screen bg-[#E4F2FF]">
      {isPrefile && !prefileAccepted && !loading && (
        <PrefileWarningModal
          onAccept={() => setPrefileAccepted(true)}
          onCancel={() => navigate(withForm(`/company/${ID}/file-form/form-summary`, formId))}
        />
      )}
      <HeaderLogged />

      {/* Progress bar */}
     <div className="bg-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap justify-center gap-3">
          {steps.map(step => {
            const isCurrent = step.id === currentStep;
            const isNext    = step.id === currentStep + 1;
            const isCompleted = step.id < currentStep;

            const baseClasses = 'text-sm font-medium px-3 py-1 border';
            const visibleClasses = isCurrent || isNext || isCompleted
              ? 'block'
              : 'hidden sm:block';

            const colorClasses = isCurrent
              ? 'bg-[#0F2C5E] text-white border-[#0F2C5E]'
              : isCompleted
                ? 'bg-green-100 text-green-700 border-green-200'
                : isNext
                  ? 'bg-[#E4E8F0] text-[#0F2C5E] border-[#E4E8F0]'
                  : 'bg-gray-100 text-gray-600 border-gray-200';

            return (
              <div
                key={step.id}
                className={`${baseClasses} ${visibleClasses} ${colorClasses}`}
              >
                {step.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 py-6 px-4 flex justify-center">
        <div className="w-full max-w-4xl bg-white border border-gray-200 shadow-md">
          {/* Processing Fee */}
          {!alreadyPaid && !paymentError && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y text-sm">
              <thead>
                <tr>
                  <th colSpan="2" className="bg-gray-600 text-white text-left px-6 py-3 uppercase">
                    Service Fee
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isPrefile && (
                  <tr className="bg-yellow-50">
                    <td colSpan="2" className="px-6 py-2 text-yellow-800 text-xs font-medium">
                      Pre-filing for tax year 2026-2027. Your return will be submitted to the IRS automatically after July 1, 2026.
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="px-6 py-3">Processing Fee</td>
                  <td className="px-6 py-3 text-right">${processingFee.toFixed(2)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th className="bg-gray-600 text-white text-left px-6 py-3">Total</th>
                  <th className="bg-gray-600 text-white text-right px-6 py-3">${processingFee.toFixed(2)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
          )}

          {/* Embedded PaymentElement (skipped on resubmit) */}
          {alreadyPaid ? (
            <form
              onSubmit={(e) => { e.preventDefault(); handleResubmitToIrs(); }}
              className="px-6 py-4"
            >
              <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 mb-4">
                This form has already been paid. Click <b>Submit to IRS</b> to
                send it again with the updated data — no additional charge.
              </div>

              {resubmitError && (
                <p className="mb-2 text-red-600 text-sm">{resubmitError}</p>
              )}

              <div className="flex items-start space-x-2 mt-2">
                <input
                  type="checkbox"
                  id="consent-resubmit"
                  className="mt-1 h-4 w-4 text-[#0F2C5E] border-gray-300 focus:ring-[#0F2C5E]"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <label htmlFor="consent-resubmit" className="text-sm">
                  I agree to the disclosure on Form 2290 and Schedule 1 / Form 8849.{' '}
                  <a
                    href={`/company/${ID}/file-form/disclosure`}
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    Read disclosure terms
                  </a>
                </label>
              </div>

              <button
                type="submit"
                disabled={resubmitting}
                className="mt-6 bg-[#0F2C5E] text-white px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resubmitting ? 'Submitting…' : 'Submit to IRS'}
              </button>
            </form>
          ) : paymentError ? (
            <div className="px-6 py-4">
              <div className="bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3" role="alert">
                {paymentError}
              </div>
            </div>
          ) : (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm
                clientSecret={clientSecret}
                consent={consent}
                onConsentChange={setConsent}
                onSuccessNavigate={() => navigate(withForm(`/company/${ID}/file-form/irs-confirmation`, formId))}
                formId={formId}
              />
            </Elements>
          )}

          {/* Back button */}
          <div className="px-6 py-4 bg-gray-50">
            <button
              onClick={handleBack}
              className="bg-gray-500 text-white px-6 py-2 hover:bg-gray-600 transition"
            >
              Back
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}